import { SubmissionReviewStatuses } from '../../../../constants/submission-review-statuses'
import { groupBy } from 'lodash'

export const normalizeFetchSimilarSubmissionsBySolutionResponse = response =>
    response.map(submission => ({
        id: submission.file_id,
        name: submission.file_name,
        pattern: {
            name: submission.category,
            feedback: submission.feedback,
            grade: submission.grade,
            lines: submission.matched_lines,
        },
        solution: submission.submission,
    }))

export const normalizeFetchSubmissionVersionsResponse = response =>
    response.map(submission => ({
        id: submission.version_id,
        submissionId: submission.id,
        name: submission.name,
        grade: Math.round(submission.grade),
        content: submission.content,
        patterns: submission.patterns.map(pattern => ({
            id: pattern.pattern_id,
            startRow: pattern.start_row,
            endRow: pattern.end_row,
        })),
        passedUnitTestsCount: submission.tests_passed,
        failedUnitTestsCount: submission.failed_tests,
        studentFeedback: submission.student_feedback
            ? {
                  liked: submission.student_feedback.liked,
                  comment: submission.student_feedback.comment,
              }
            : null,
    }))

export const normalizeFetchSubmissionsSummaryResponse = response => ({
    submissions: response.submissions.map(submission => ({
        name: submission.file_name,
        grade: Math.round(submission.grade),
        instantFeedbackUrl: submission.feedback_url,
    })),
    totalPages: response.total_pages,
})

const normalizeReview = review => ({
    reviewer: {
        id: review.reviewed_by_user.id,
        name: review.reviewed_by_user.name,
        email: review.reviewed_by_user.email,
    },
    approveStatus: review.ai_accepted,
    submissionId: review.file_id,
    reviewedAt: review.created_at,
    oldPatternId: review.old_pattern_id,
    newPatternId: review.new_pattern_id,
    oldReasoning: Array.isArray(review.old_reasoning) ? review.old_reasoning[0] : null,
    newReasoning: Array.isArray(review.new_reasoning) ? review.new_reasoning[0] : null,
})

export const normalizeReviewSubmissionResponse = response => ({
    review: normalizeReview(response.file_review),
    newClusterId: response.new_cluster_id,
})

export const normalizeFetchSubmissionReviewsResponse = response =>
    response.map(normalizeReview).sort((a, b) => new Date(b.reviewedAt) - new Date(a.reviewedAt))

export const normalizeFetchTextAssignmentSubmissionsResponse = response =>
    response.map(submission => ({
        id: submission.id,
        studentId: submission.student_id,
        reviewStatus: submission.review_status,
        escalated: submission.escalated,
        internalNote: submission.internal_note,
    }))

export const normalizeFetchQuestionAnswersResponse = response =>
    // Backend returns only the pattern_match for the requested questionRubricId.
    // One row per submission (student).
    response.map(submission => {
        const match = submission.pattern_matches?.[0]
        
        // Find the escalation that matches this question_rubric_id (if any)
        const escalation = submission.escalations?.find(
            esc => esc.question_rubric_id === match?.question_rubric_id
        )
        
        return {
            id: submission.assignment_version_student_id,
            answerId: submission.id,
            studentId: submission.student_id,
            escalated: escalation?.status ?? null,
            internalNote: escalation?.internal_note ?? null,
            assigneeId: submission.reviewer_id,
            questionRubricId: match?.question_rubric_id || null,
            categoryId: match?.pattern_id || null,
            approveStatus: match?.approved ?? null,
            actor: match?.actor || null,
            reviewStatus: match?.approved
                ? SubmissionReviewStatuses.REVIEWED
                : SubmissionReviewStatuses.UNREVIEWED,
            confidence: match?.confidence_band || null,
        }
    })

export const normalizeFetchTextSubmissionDetailsResponse = (response, submissionId) => ({
    id: submissionId,
    feedback: response.feedback,
    answers: response.submissions
        .flatMap(submission => {
            const baseAnswer = {
                id: submission.id,
                questionId: submission.question_id,
                content: submission.text,
                feedback: submission.feedback,
                assigneeId: submission.reviewer_id,
                submissionId: submissionId,
                inTestSet: submission.in_test_set,
            }

            // If no pattern matches, return base answer with null grading fields
            if (!submission.pattern_matches?.length) {
                // Find escalation for this submission (if any) - use first escalation if exists
                const escalation = submission.escalations?.[0]
                
                return [{
                    ...baseAnswer,
                    questionRubricId: null,
                    reasoning: null,
                    categoryId: null,
                    approveStatus: null,
                    actor: null,
                    confidence: null,
                    createdAt: null,
                    trainingRunId: null,
                    highlights: null,
                    explainabilityReasoning: null,
                    ethicalCheckOutcome: null,
                    isAdequate: true,
                    escalated: escalation?.status ?? submission.escalated,
                    note: escalation?.internal_note ?? submission.internal_note,
                }]
            }

            // Create one answer per pattern_match (per questionRubric)
            return submission.pattern_matches.map(match => {
                // Find the escalation that matches this question_rubric_id
                const escalation = submission.escalations?.find(
                    esc => esc.question_rubric_id === match.question_rubric_id
                )
                
                return {
                    ...baseAnswer,
                    questionRubricId: match.question_rubric_id,
                    reasoning: match.reasoning,
                    categoryId: match.pattern_id || null,
                    approveStatus: match.approved,
                    actor: match.actor,
                    confidence: match.confidence_band,
                    createdAt: match.created_at,
                    trainingRunId: match.training_run_id,
                    highlights: match.explanation?.highlights
                        ? normalizeHighlights(match.explanation.highlights)
                        : null,
                    explainabilityReasoning: match.explanation?.reasoning,
                    ethicalCheckOutcome: match.explanation?.ethical_check_outcome,
                    isAdequate: match.explanation?.is_classification_adequate ?? true,
                    escalated: escalation?.status ?? null,
                    note: escalation?.internal_note ?? null,
                    personalizedFeedback: match.personalized_feedback || null,
                    cannedFeedback: match.canned_feedback || null,
                }
            })
        })
        .sort((a, b) => a.questionId - b.questionId),
})

export const normalizeFetchSimilarAnswersResponse = response =>
    response.map(similarAnswer => ({
        categoryId: similarAnswer.pattern_id,
        id: similarAnswer.submission_id,
        name: similarAnswer.submission_name,
        text: similarAnswer.text,
        confidence: similarAnswer.confidence,
    }))

export const normalizeFetchAnswerCategoryHistoryResponse = response =>
    response.map(historyItem => ({
        id: historyItem.submission_id,
        categoryId: historyItem.pattern_id,
        confidence: historyItem.confidence_band,
        approveStatus: historyItem.approved,
        actor: historyItem.actor,
        deleted: historyItem.deleted,
        createdAt: historyItem.created_at,
        trainingRunId: historyItem.training_run_id,
        patternName: historyItem.pattern_name,
        reasoning: historyItem.reasoning || historyItem.explanation?.reasoning,
    }))

export const normalizeFetchAnswerCategoryMismatchPredictionsResponse = response =>
    Object.entries(response).map(([answerId, mismatch]) => ({
        id: mismatch.id,
        resolved: mismatch.resolved,
        answerId: parseInt(answerId),
        categoryScore: mismatch.pattern_score,
        currentCategoryId: mismatch.original_pattern_id,
        predictedCategoryId: mismatch.predicted_pattern_id,
    }))

export const normalizeFetchEscalatedAnswersResponse = response =>
    groupBy(
        response.map(answer => ({
            id: answer.submission_id,
            submissionId: answer.version_student_id,
            studentId: answer.student_id,
            questionId: answer.question_id,
            questionRubricId: answer.question_rubric_id,
            rubricId: answer.rubric_id,
            note: answer.internal_note,
            status: answer.status,
            user: {
                id: answer.reviewer?.id,
                name: answer.reviewer?.name,
            },
            escalatedBy: answer.escalated_by ? {
                id: answer.escalated_by.id,
                name: answer.escalated_by.name,
            } : null,
        })),
        'questionId',
    )

export const normalizeHighlights = highlights =>
    highlights.map(highlight => ({
        start: highlight.start_col,
        end: highlight.end_col,
        importance: highlight.importance ?? 1,
    }))

export const denormalizeHighlights = highlights =>
    highlights.map(highlight => ({
        start_col: highlight.start,
        end_col: highlight.end,
        importance: highlight.importance ?? 1,
    }))

export const normalizeFetchExplanationResponse = (response, args) => ({
    isAdequate: response.is_classification_adequate,
    categoryId: args?.categoryId ?? response.explained_category_id,
    reasoning: response.reasoning,
    highlights: normalizeHighlights(response.highlights),
    ethicalCheckOutcome: response.ethical_check_outcome,
})

export const normalizeFetchSubmissionAttachmentsResponse = (response) =>
    response.map(attachment => ({
        id: attachment.id,
        submissionId: attachment.assignment_version_student_id,
        questionId: attachment.question_id,
        createdAt: attachment.created_at,
        name: attachment.name,
    }))